// EdAI Suites page content. House rules: no em-dashes; no competitor named
// alongside a negative result (the proof-point study stays structural, per the
// brief); client boards are not named on the marketing site; internal revenue
// figures stay internal. Production and psychometric figures are from the 2025
// production implementation.

// The interactive specimen: the visitor sits in the expert reviewer's chair.
// A drafted item arrives triaged by the classifier, with its designated source
// passage alongside. The visitor decides; the audit trail records it. The
// classifier ordered the attention. It never decided.
export const reviewSpecimen = {
  queueLabel: 'Review queue \u00b7 ranked by classifier',
  triageNote: 'Triage rank 1 of 12. Flag: verify the timing window in option A against the source passage.',
  item: {
    stem:
      'A 44-year-old woman undergoes tumescent liposuction of the abdomen and flanks. Eighteen hours later she develops dyspnea, tachycardia, confusion, and a petechial rash over the chest and axillae. Which of the following is the most likely diagnosis?',
    options: [
      { key: 'A', text: 'Pulmonary thromboembolism' },
      { key: 'B', text: 'Fat embolism syndrome' },
      { key: 'C', text: 'Transfusion-related acute lung injury' },
      { key: 'D', text: 'Local anesthetic systemic toxicity' },
    ],
    correctKey: 'B',
  },
  sourcePassage:
    'Fat embolism syndrome typically presents 12 to 72 hours after long-bone trauma or liposuction with the classic triad of hypoxemia, neurologic impairment, and a petechial rash; thromboembolism rarely produces petechiae and classically presents later in the postoperative course.',
  sourceLine: 'Designated source \u00b7 board reading list \u00b7 claim verified against source, 2 of 2',
  baseTrail: [
    { event: 'Source registered', detail: 'Designated source ingested; claims extracted with verbatim provenance' },
    { event: 'Item drafted', detail: 'Generated from certified claims; style and difficulty per blueprint' },
    { event: 'Classifier triage', detail: 'Ranked 1 of 12 for review; option A flagged for source verification' },
  ],
  decisions: {
    approve: { event: 'Human decision: approved', detail: 'Expert physician approved the item. Recorded, dated, defensible.' },
    revise: { event: 'Human decision: revision requested', detail: 'Expert edits recorded; item returns to the queue with its chain intact.' },
    reject: { event: 'Human decision: rejected', detail: 'Item retired. The rejection is part of the record too.' },
  },
};

export const productionStats = [
  { value: '87%', label: 'Reduction in expert physician time per item: 15 to 20 minutes against 2 to 4 hours traditionally' },
  { value: '0', label: 'AI-attributed factual errors in production' },
  { value: '0', label: 'Validity challenges from regulators or accreditors on deployed items' },
  { value: '400+', label: 'Validated items delivered in the 2025 examination cycle, at 100% client retention' },
];

export const psychometrics = [
  { metric: 'Item-writing guideline compliance', edai: '94%', benchmark: '76% traditional' },
  { metric: 'Mean difficulty (p-value)', edai: '0.68', benchmark: 'target 0.60 to 0.75' },
  { metric: 'Mean discrimination', edai: '0.32', benchmark: 'target above 0.25' },
  { metric: 'Internal consistency (\u03b1)', edai: '0.89', benchmark: 'excellent range' },
  { metric: 'Items flagged for revision', edai: '8%', benchmark: '12% traditional' },
  { metric: 'Expert rating on higher-order items', edai: '92% equivalent or superior', benchmark: 'blinded expert review' },
  { metric: 'Differential item functioning across demographic groups', edai: 'None detected', benchmark: '' },
];

export const proofStudy = {
  headline: 'Open book. Below passing.',
  body:
    'The leading general-purpose medical AI, one that aces medical licensing exams, was given 185 EdAI certification items under open-book conditions with direct access to the source articles. It scored 68.65% overall: below the 70% certification passing threshold, with specialty accuracy ranging from 55% to 79%. Two specialties failed nearly half the questions with the answers\u2019 source material in hand.',
  implication:
    'Content access is not content mastery. Certification-grade items demand clinical synthesis beyond retrieval, and that is a property of how the items are built.',
};

export const corpus = {
  intro:
    'Underneath the suite sits a certified clinical knowledge base: physician-reviewed source documents turned into a structured graph of atomic claims. Nothing uncertified is ever served, every claim carries its exact source sentence, and every set of facts declares whether it is complete or a sample, so an answer key is never built on a guess.',
  stats: [
    { value: '159,661', label: 'Live certified claims, each certified by a named, accountable physician' },
    { value: '954', label: 'Physician-authored source reviews behind them' },
    { value: '14,715', label: 'Medical concepts connected by 16,496 certified relationships' },
    { value: '7', label: 'National exam targets lensed from one corpus without duplicating knowledge' },
  ],
};

export const modules = [
  {
    name: 'Item generation',
    text: 'Certification-grade multiple-choice drafting from designated sources, calibrated across the expertise spectrum from resident foundation to subspecialty focus, queued for expert review in triage order.',
    shot: { src: '/screenshots/generate-mcq.png', alt: 'Item generation interface', caption: 'Item generation from designated sources' },
  },
  {
    name: 'The item bank',
    text: 'Every item with its review status, psychometric history, and full chain from source passage to approved form. When an item is challenged, the chain is the answer.',
    shot: { src: '/screenshots/item_bank.png', alt: 'Item bank management', caption: 'The item bank, with review status' },
  },
  {
    name: 'Oral examination preparation and simulation',
    text: 'Structured oral examination practice built on the same corpus as the written examination, so the viva and the written test rest on one standard.',
    shot: { src: '/screenshots/mod2-orals-prep.png', alt: 'Oral exam preparation module', caption: 'Oral examination preparation' },
  },
  {
    name: 'Learning management',
    text: 'A purpose-built medical LMS: courses and learning paths with prerequisite sequencing, CME allocation, certificates with verification codes, and instructor analytics.',
    shot: { src: '/screenshots/lms1.png', alt: 'Learning management system', caption: 'The medical LMS' },
  },
  {
    name: 'Fact checking and content review',
    text: 'Reviewer workflows that hold every claim against its designated source before it enters the item pool. A hallucinated source span is treated as fabricated provenance, and rejected.',
    shot: { src: '/screenshots/mod2-fact-checker.png', alt: 'Fact checking module', caption: 'Claim-level content review' },
  },
  {
    name: 'Program analytics',
    text: 'Item performance, reviewer throughput, and examination composition against the blueprint, in one view built for an exam committee chair.',
    shot: { src: '/screenshots/analyze-dashboard.png', alt: 'Analytics dashboard', caption: 'Program analytics' },
  },
  {
    name: 'MOC portfolio',
    text: 'Contribution-based Maintenance of Certification infrastructure supporting a three-pathway recertification framework: continuous MOC, traditional examination, and reciprocal recognition.',
    shot: null,
  },
  {
    name: 'Secure testing platform',
    text: 'In development: secure delivery, identity verification, AI-assisted proctoring, accommodation support, automated scoring, and psychometric analytics, natively integrated with the item bank.',
    shot: { src: '/screenshots/administer-proctoring.png', alt: 'Examination administration', caption: 'Examination administration (secure platform in development)' },
  },
];

export const qaPipeline = [
  { name: 'Automated checks', text: 'Content verification requiring 100% fact fidelity, guideline compliance, and clinical coherence, before any human sees the draft.' },
  { name: 'Level 1: the content expert', text: 'A board-certified specialist verifies clinical accuracy, difficulty, and distractor plausibility.' },
  { name: 'Level 2: the assessment expert', text: 'A psychometrician checks item-writing quality, cueing, cognitive level, and bias.' },
  { name: 'Level 3: editorial', text: 'Grammar, style consistency, format, and citation verification.' },
  { name: 'Pilot testing', text: 'Field administration with psychometric data collection: p-value, discrimination, comparison against traditional items, then refinement or retirement.' },
];

export const qaBar =
  'Approval requires 100% clinical accuracy, guideline compliance of at least 95%, and expert consensus. Classifiers triage and order attention at every stage; they never gate and never decide.';

export const video = {
  embed: null, // paste https://www.youtube.com/embed/VIDEO_ID when the pipeline film is ready
  title: 'See the item pipeline',
  text: 'A walkthrough of the path from designated source to approved examination item: generation, triage, expert review, and the audit trail.',
};

export const faq = [
  {
    q: 'Does this replace our item writers?',
    a: 'It replaces the blank page, not the expert. Volunteer item writing costs 2 to 4 expert hours per item; here the expert spends 15 to 20 minutes deciding, editing, and approving, with the classifier ordering the queue. Every consequential decision remains a physician\u2019s, by rule.',
  },
  {
    q: 'What happens when an item is challenged?',
    a: 'The chain is the answer. Every published item traces from the designated source passage through generation, triage, and named expert review to its approved form, with dates. A challenge is answered from the record, not from anyone\u2019s recollection.',
  },
  {
    q: 'Whose content is this built on?',
    a: 'Yours. Your board designates the sources; the corpus operationalizes them with verbatim provenance and physician certification. EdAI never decides what knowledge is valid; the authorities do. That is the architecture, not a slogan.',
  },
  {
    q: 'How do we know the items measure up psychometrically?',
    a: 'The 2025 production implementation met or exceeded traditional benchmarks: 94% item-writing guideline compliance against 76% traditionally, mean difficulty 0.68 inside the target band, discrimination 0.32 above the 0.25 threshold, internal consistency of 0.89, fewer items flagged for revision than traditional authoring, and no differential item functioning detected across demographic groups.',
  },
  {
    q: 'Can it write beyond initial certification?',
    a: 'Yes. The same repository generates items across the expertise spectrum: resident foundation, initial certification, advanced practice, subspecialty focus, and remediation. That calibration is what makes continuous-competency and MOC assessment practical, and it is structurally impossible under volunteer item-writing models.',
  },
  {
    q: 'What does an engagement look like?',
    a: 'Annual licensing per board, an implementation phase that builds your content repository from your designated sources, and advisory support for content curation, psychometric validation, and blueprint development where you want it. Talk to us and we will scope it against your examination calendar.',
  },
];
